import axios from "axios";
import qs from "qs";
import SessionStorageCache from "./cache/SessionStorageCache";
import LocalStorageCache from "./cache/LocalStorageCache";
import HttpAccountRepository from "./services/authentication/HttpAccountRepository";
import HttpAuthorizationStore from "./services/authorization/HttpAuthorizationStore";
import ListerSuggester from "./services/suggestion/ListerSuggester";
import HttpLister from "./services/listing/HttpLister";
import HttpProvider from "./services/HttpProvider";
import HttpStore from "./services/persistence/HttpStore";
import HttpAccessor from "./services/persistence/HttpAccessor";
import MemoryCache from "./cache/MemoryCache";
import CachingProviderShim from "./services/CachingProviderShim";
import { defaults } from "react-chartjs-2";
import HttpEvaluator from "./services/HttpEvaluator";
import DependentGenerationService from "./services/curves/DependentGenerationService";
import AggregateTotalService from "./services/acoustic/AggregateTotalService";
import CurveTestService from "./services/curves/CurveTestService";
import NonResidentialErPService from "./services/erp/NonResidentialErPService";
import ResidentialErPService from "./services/erp/ResidentialErPService";
import paths from "./services/paths";
import PitchAngleGenerationService from "./services/curves/PitchAngleGenerationService";

function Startup(config) {
  defaults.global = { ...defaults.global, ...config.chartjs.defaults.global };

  const http = axios.create({
    baseURL: "/api/",
    https: true,
    paramsSerializer: (params) => qs.stringify(params, { allowDots: true, skipNulls: true }),
  });

  http.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response) {
        error.localization = error.response.data?.localization ?? {
          id: "app.general_error",
          defaultMessage: "Something went wrong. If the problem persists, please contact your administrator.",
        };
        error.traceId = error.response.data?.traceId;
        error.validation = error.response.data?.validation;
      } else if (!axios.isCancel(error)) {
        error.localization = { id: "app.network_error", defaultMessage: "A network error occurred. Please try again." };
      }
      return Promise.reject(error);
    },
  );

  const localSessionCache = new SessionStorageCache();
  const localCache = new LocalStorageCache();
  const memoryCache = new MemoryCache();
  const authorizationStore = new HttpAuthorizationStore(http, localCache);
  const accountRepository = new HttpAccountRepository(http, authorizationStore);
  const maintenanceStatusProvider = new HttpProvider(http, paths.MAINTENANCE_STATUS_PATH);

  const seriesLister = new HttpLister(http, "curve/series/list");
  const serieComercialLister = new HttpLister(http, "product/series/list");
  const accessorySeriesLister = new HttpLister(http, "accessory/series/list");
  const accessoryModelLister = new HttpLister(http, "accessory/models/list");
  const accessoryResultsLister = new HttpLister(http, "accessory/results/list");
  const serieComercialLoadingsLister = new HttpLister(http, "product/series/loadings/list");
  const serieComercialLoadingsQueueLister = new HttpLister(http, "product/series/loadings/queue/list");
  const serieComercialLoadingStatusLister = new HttpLister(http, "product/series/loadings/status/list");
  const serieSAPLister = new HttpLister(http, "product/series/sap/list");
  const productSAPLister = new HttpLister(http, "product/sap/list");
  const serieComercialGenericProductLister = new HttpLister(http, "product/generic-product-series/list");
  const serieComercialProductLister = new HttpLister(http, "product/product-series/list");
  const productLister = new HttpLister(http, "curve/product/list");
  const curveGroupLister = new HttpLister(http, "curve/curve-group/list");
  const curveLister = new HttpLister(http, "curve/list");
  const acousticCurveLister = new HttpLister(http, "curve/acoustic-curve/list");
  const modeloCurveLister = new HttpLister(http, "curve/modelo/list");
  const modeloGroupCurveLister = new HttpLister(http, "curve/group/list");
  const curveTestLister = new HttpLister(http, "curve/test/list");
  const curveTestProvider = new HttpProvider(http, "curve/test", (key) => ({ testId: key.testId, curveId: key.curveId }));
  const etimLister = new HttpLister(http, "etim/series/list");
  const seriesSuggester = new ListerSuggester(
    seriesLister,
    (f) => ({ name: f }),
    (s) => s.name,
  );
  const serieComercialSuggester = new ListerSuggester(
    serieComercialLister,
    (f) => ({ name: f }),
    (s) => s.name,
  );
  const serieComercialAccessorySuggester = new ListerSuggester(
    accessorySeriesLister,
    (f) => ({ series: f }),
    (s) => s.series,
  );
  const serieComercialAccessoryNameSuggester = new ListerSuggester(
    accessoryModelLister,
    (f) => ({ name: f }),
    (s) => s.name,
  );
  const serieComercialLoadingsSuggester = new ListerSuggester(
    serieComercialLoadingsLister,
    (f) => ({ serieComercial: f }),
    (s) => s.serieComercial,
  );
  const serieComercialSAPSuggester = new ListerSuggester(
    serieSAPLister,
    (f) => ({ name: f }),
    (s) => s.name,
  );
  const productSAPSuggester = new ListerSuggester(
    productSAPLister,
    (f) => ({ code: f }),
    (s) => s.code + " - " + s.description,
    99,
  );
  const serieComercialInternalSuggester = new ListerSuggester(
    serieComercialLister,
    (f) => ({ internalName: f }),
    (s) => s.internalName,
  );
  const seriesGenericProductsSuggester = new ListerSuggester(
    serieComercialGenericProductLister,
    (f) => ({ name: f }),
    (s) => s.name,
  );
  const seriesProductsSuggester = new ListerSuggester(
    serieComercialProductLister,
    (f) => ({ name: f }),
    (s) => s.name,
  );
  const productSuggester = new ListerSuggester(
    productLister,
    (f) => ({ name: f }),
    (s) => s.name,
  );
  const curveGroupSuggester = new ListerSuggester(
    curveGroupLister,
    (f) => ({ name: f }),
    (s) => s.name,
  );
  const curveSuggester = new ListerSuggester(
    curveLister,
    (f) => ({ name: f }),
    (s) => s.name,
  );
  const curveLabTestNumberSuggester = new ListerSuggester(
    curveTestLister,
    (f) => ({ labTestNumber: f }),
    (s) => s.labTestNumber,
  );
  const curveTestIdSuggester = new ListerSuggester(
    curveTestLister,
    (f) => ({ curveTestId: f }),
    (s) => s.curveTestId,
  );
  const etimSuggester = new ListerSuggester(
    etimLister,
    (f) => ({ classETIM: f }),
    (s) => s.classETIM,
  );

  const curveSpecificationAccessor = new HttpAccessor(http, "curve/specification", (key) => ({ id: key }));
  const curveTestService = new CurveTestService(http);
  const modeloCurveSpecificationAccessor = new HttpAccessor(http, "curve/modelo/specification", (key) => ({ id: key }));
  const modeloCurveGroupSpecificationAccessor = new HttpAccessor(http, "curve/group/specification", (key) => ({ id: key }));
  const dependentGenerationService = new DependentGenerationService(http, "curve/test/dependents/process", 1000);

  const aeroTestLister = new HttpLister(http, "tests/aerodynamic/list");
  const acousticTestLister = new HttpLister(http, "tests/acoustic/list");
  const aggregateTotalService = new AggregateTotalService(http);

  const kitErPLister = new HttpLister(http, "erp/kit/list");
  const rvuErPStore = new HttpStore(http, "erp/rvu", (key) => ({ name: key }));
  const rvuPerformanceEvaluator = new HttpEvaluator(http, "erp/rvu/performance");
  const nonResidentialErPService = new NonResidentialErPService(http);
  const residentialErPService = new ResidentialErPService(http);

  const motorLister = new HttpLister(http, "motor/list");
  const motorProvider = new HttpProvider(http, "motor", (key) => ({ id: key }));

  const productCurveAttributesProvider = new CachingProviderShim(
    new HttpProvider(http, "product/inputs/curve/attributes", (key) => ({ concept: key })),
    memoryCache,
    "Genesis.Product.CurveAttributes",
  );
  const productConceptsProvider = new CachingProviderShim(
    new HttpProvider(http, "product/inputs/concepts", (key) => ({ codSerieComercial: key })),
    memoryCache,
    "Genesis.Product.Concepts",
  );
  const productTechnicalAttributesProvider = new CachingProviderShim(
    new HttpProvider(http, "product/inputs/technical/attributes", (key) => ({ concept: key })),
    memoryCache,
    "Genesis.Product.TechnicalAttributes",
  );
  const productCurveGroupAttributesProvider = new CachingProviderShim(
    new HttpProvider(http, "product/inputs/curve-group/attributes", (key) => ({ concept: key })),
    memoryCache,
    "Genesis.Product.CurveGroupAttributes",
  );
  const curveGroupDetailsProvider = new CachingProviderShim(
    new HttpProvider(http, "product/curve-groups", (key) => ({ codModeloProductoGenerico: key })),
    memoryCache,
    "Genesis.Product.Concepts",
  );

  const productSpecificationAccessor = new HttpAccessor(http, "product/specification", (key) => ({ id: key.id, codSerieComercial: key.seriesId, version: key.version }));
  const productSpecificationStore = new HttpStore(http, "product/specifications", (key) => ({ specifications: key.specifications }));

  const accessorySpecificationAccessor = new HttpAccessor(http, "accessory/specification", (key) => ({ id: key.id, codSerieComercial: key.seriesId, version: key.version }));
  const accessorySpecificationStore = new HttpStore(http, "accessory/specifications", (key) => ({ specifications: key.specifications }));
  const newAccessoryClassRecorder = new HttpStore(http, "accessory/newclass/recorder", (key) => ({ newAccessoryClass: key.newAccessoryClass }));

  const seriesLoadingProcessor = new HttpStore(http, "product/series/loadings/process", (key) => ({ seriesProcess: key.seriesProcess }));
  const etimLegacyProcessor = new HttpAccessor(http, "product/curves/loadings/process");

  // Admin
  const crmBulkProcessor = new HttpAccessor(http, "admin/crm/bulk/process");
  const seriesExpressProcessor = new HttpAccessor(http, "admin/series-express/process");
  const pdfBulkProcessor = new HttpAccessor(http, "admin/certificate/bulk/upload");

  const productSAPSpecificationAccessor = new HttpAccessor(http, "product/sap/specification", (key) => ({ id: key }));
  const accessoryDescriptonAccessor = new HttpAccessor(http, "accessory/description", (key) => ({ code: key }));

  const neededAttributesProvider = new CachingProviderShim(
    new HttpProvider(http, "product/inputs/needed-attributes", (key) => ({ concept: key })),
    memoryCache,
    "Genesis.Product.NeededAttributes",
  );
  const productExistenceProvider = new CachingProviderShim(new HttpProvider(http, "product/exists", (key) => ({ model: key })), memoryCache, "Genesis.Product.ProductExistence");
  const accessoryExistenceProvider = new CachingProviderShim(
    new HttpProvider(http, "accessory/exists", (key) => ({ model: key })),
    memoryCache,
    "Genesis.Product.AccessoryExistence",
  );
  const accessoryExistenceBdPProvider = new CachingProviderShim(
    new HttpProvider(http, "accessory/exists/bdp", (key) => ({ model: key })),
    memoryCache,
    "Genesis.Product.AccessoryExistenceBdp",
  );
  const seriesExistenceProvider = new HttpProvider(http, "accessory/series/exists", (key) => ({ model: key }));
  const curveExistenceProvider = new CachingProviderShim(new HttpProvider(http, "curve/exists", (key) => ({ model: key })), memoryCache, "Genesis.Product.CurveExistence");
  const grupoCurveExistenceProvider = new CachingProviderShim(
    new HttpProvider(http, "curve/group/exists", (key) => ({ model: key })),
    memoryCache,
    "Genesis.Product.GrupoCurveExistence",
  );

  const companiesProvider = new HttpProvider(http, "product/companies");

  const plateTemplatesProvider = new HttpProvider(http, "plate/templates");
  const plateTemplateSectionsProvider = new HttpProvider(http, "plate/templates/sections", (key) => ({
    codModeloProducto: key.codModeloProducto,
    codProductoComercial: key.codProductoComercial,
    key: key.template,
  }));
  const plateSectionsCloneProvider = new HttpProvider(http, "plate/templates/sections/clone", (key) => ({
    codProductoComercial: key.codProductoComercial,
    codeTemplate: key.codeTemplate,
  }));
  const plateSectionsProvider = new HttpProvider(http, "plate/sections", (key) => ({ product: key }));
  const productTestsProvider = new HttpProvider(http, "plate/product-tests", (key) => key);
  const plateProductTestLister = new HttpLister(http, "plate/product-tests/list");
  const plateExistingTemplateLister = new HttpLister(http, "plate/existing-templates/list");
  const plateIconsProvider = new HttpProvider(http, "plate/icons", (key) => key);
  const plateProductTestsProvider = new HttpProvider(http, "plate/product-tests/ccp", (key) => key);
  const plateProductAttributesProvider = new HttpProvider(http, "plate/product/attributes", (key) => ({ id: key }));
  const plateProductAndTestAttributesProvider = new HttpProvider(http, "plate/product-tests/attributes", (key) => key);
  const plateSummaryProvider = new HttpProvider(http, "plate/summary", (key) => ({ id: key }));
  const plateUniqueAttributesProvider = new HttpProvider(http, "plate/attributes/unique");

  const plateTemplateStore = new HttpStore(http, "plate/template", (key) => ({ template: key }));
  const plateTemplatesStore = new HttpStore(http, "plate/templates", (key) => ({ template: key }));

  const certificateManufacturersProvider = new HttpProvider(http, "certificate/manufacturers");
  const certificateEntitiesProvider = new HttpProvider(http, "certificate/entities");
  const certificateExistingSeriesProvider = new HttpProvider(http, "certificate/existing-series", (key) => ({ keyCompany: key }));
  const certificateTemplatesProvider = new HttpProvider(http, "certificate/templates", (key) => key);
  const certificateStore = new HttpStore(http, "certificate/save", (key) => ({ certificate: key }));
  const certificateThirdPartyStore = new HttpStore(http, "certificate/save/thirdparty", (key) => ({ certificate: key }));
  const certificateThirdPartyUpdateStore = new HttpStore(http, "certificate/update/thirdparty", (key) => ({ certificate: key }));
  const certificateUpdate = new HttpStore(http, "certificate/update", (key) => ({ certificate: key }));
  const certificatePreview = new HttpStore(http, "certificate/preview", (key) => ({ certificate: key }), "arraybuffer");
  const certificatePdfStore = new HttpStore(http, "certificate/upload-pdf", (key) => ({ certificate: key }));
  const certificateExpiryStore = new HttpStore(http, "certificate/expiry", (key) => ({ certificate: key }));
  const certificateGenerateProvider = new HttpProvider(http, "certificate/generate", (key) => key, "arraybuffer");
  const certificateListProvider = new HttpProvider(http, "certificate/list", (key) => key);
  const certificateCloneListProvider = new HttpProvider(http, "certificate/list/clone", (key) => key);
  const certificateSeriesProductsProvider = new HttpProvider(http, "certificate/series-products", (key) => key);
  const certificateLanguagesProvider = new HttpProvider(http, "certificate/languages");
  const certificateLegislationsProvider = new HttpProvider(http, "certificate/legislations");
  const certificateLegislationTypesProvider = new HttpProvider(http, "certificate/legislations/types");
  const certificateRegulationsProvider = new HttpProvider(http, "certificate/regulations", (key) => ({ idRegulation: key }));

  const certificateSearchProvider = new CachingProviderShim(
    new HttpProvider(http, "certificate/search", (key) => ({ keyCompany: key.keyCompany, series: key.series })),
    memoryCache,
    "Genesis.Certificate.Search",
  );
  //const certificateSearchProvider = new HttpStore(http, "certificate/search", (key) => ({ search: key }));

  const certificateLinkedSeriesProvider = new HttpStore(http, "certificate/linked-series", (key) => ({ series: key }));
  const certificateExistingProvider = new HttpProvider(http, "certificate/existing", (key) => key);
  const certificateExistingThirdPartyProvider = new HttpProvider(http, "certificate/existing/thirdparty", (key) => key);
  const certificateDocumentTypesProvider = new HttpProvider(http, "certificate/document/types");
  const certificateRegulationsByDocumentTypesProvider = new HttpProvider(http, "certificate/document/types/regulations", (key) => ({ idTipoDocumental: key }));
  const certificateRemoval = new HttpAccessor(http, "certificate/remove", (key) => key);
  const certificateIdNotaCertProvider = new HttpProvider(http, "certificate/notacert", (key) => ({ idNotaCert: key }));
  const certificateNotaCertStore = new HttpStore(http, "certificate/notacert", (key) => ({ certificate: key }));

  const certificateNotaCodeProvider = new HttpProvider(http, "certificate/exists/nota-code", (key) => ({ code: key }));
  const certificateNotaNameProvider = new HttpProvider(http, "certificate/exists/nota-name", (key) => ({ name: key }));

  const certificateRegulationExistenceProvider = new HttpProvider(http, "certificate/exists/regulations", (key) => ({ regulation: key }));
  const certificateDocumentTypeExistenceProvider = new HttpProvider(http, "certificate/exists/documentType", (key) => ({ documentType: key }));
  const certificateRegulationStore = new HttpStore(http, "certificate/save/regulation", (key) => ({ regulationInput: key }));
  const certificateDocumentTypeStore = new HttpStore(http, "certificate/save/documentType", (key) => ({ documentTypeInput: key }));

  const dashboardUnassignedProductsLister = new HttpLister(http, "dashboard/unassigned-products/list");

  const aiConversationMessenger = new HttpStore(http, "ai/message", (key) => key);
  const aiConversationProcessor = new HttpStore(http, "ai/process", (key) => key);

  const etimVersionsProvider = new HttpProvider(http, "etim/versions");
  const etimListProvider = new HttpProvider(http, "etim/list", (key) => ({ version: key }));
  const etimSeriesProvider = new HttpProvider(http, "etim/series", (key) => key);
  const etimClassSeriesProvider = new HttpProvider(http, "etim/seriesetimclass", (key) => key);
  const etimFeaturesProvider = new HttpProvider(http, "etim/features", (key) => ({ version: key.version, id: key.id }));
  const etimCommonFeaturesProvider = new HttpProvider(http, "etim/features/common", (key) => ({ version: key.version, eTIMClasses: key.etimClasses }));
  const etimProductsProvider = new HttpProvider(http, "etim/series/products", (key) => key);
  const etimGroupedProductsProvider = new HttpProvider(http, "etim/series/product/groups", (key) => key);
  const etimCharacteristicsProvider = new HttpProvider(http, "etim/characteristics", (key) => key);
  const etimStore = new HttpStore(http, "etim/post", (key) => key);
  const etimClassCommercialExistenceProvider = new HttpProvider(http, "etim/etim-class-commercial/exists", (key) => ({ etimClassCommercial: key }));

  const bmeCatLanguagesProvider = new HttpProvider(http, "etim/bme-cat/languages", (key) => key);
  const bmeCatCompaniesProvider = new HttpProvider(http, "etim/bme-cat/companies", (key) => key);
  const bmeCatProductsProvider = new HttpProvider(http, "etim/bme-cat/products", (key) => key);
  const bmeCatXMLStructureProvider = new HttpProvider(http, "etim/bme-cat/xml-structure", (key) => key);
  const bmeCatStore = new HttpStore(http, "etim/bme-cat/post", (key) => key);

  const voltagesProvider = new HttpProvider(http, "product/voltages");
  const manufacturerCompaniesProvider = new HttpProvider(http, "accessory/companies");
  const accessoryCategoriesProvider = new HttpProvider(http, "accessory/categories");
  const accessoryClassProductsProvider = new HttpProvider(http, "accessory/class/products", (key) => ({ code: key }));

  const pitchAngleGenerationService = new PitchAngleGenerationService(http, "curve/test/pitch-angle/process", 1000);
  return {
    localSessionCache,
    localCache,
    memoryCache,
    authorizationStore,
    accountRepository,
    maintenanceStatusProvider,
    seriesLister,
    serieComercialLister,
    accessoryResultsLister,
    serieComercialLoadingsLister,
    serieComercialLoadingsQueueLister,
    serieComercialLoadingStatusLister,
    serieSAPLister,
    serieComercialGenericProductLister,
    serieComercialProductLister,
    productLister,
    curveGroupLister,
    curveLister,
    acousticCurveLister,
    modeloCurveLister,
    modeloGroupCurveLister,
    curveTestLister,
    curveTestProvider,
    seriesSuggester,
    serieComercialSuggester,
    serieComercialLoadingsSuggester,
    serieComercialInternalSuggester,
    seriesGenericProductsSuggester,
    serieComercialAccessorySuggester,
    serieComercialAccessoryNameSuggester,
    seriesProductsSuggester,
    serieComercialSAPSuggester,
    productSAPSuggester,
    productSuggester,
    curveGroupSuggester,
    curveSuggester,
    curveLabTestNumberSuggester,
    curveTestIdSuggester,
    curveSpecificationAccessor,
    curveTestService,
    modeloCurveSpecificationAccessor,
    modeloCurveGroupSpecificationAccessor,
    accessoryDescriptonAccessor,
    dependentGenerationService,
    acousticTestLister,
    aggregateTotalService,
    aeroTestLister,
    kitErPLister,
    nonResidentialErPService,
    residentialErPService,
    rvuErPStore,
    rvuPerformanceEvaluator,
    motorLister,
    motorProvider,
    productCurveAttributesProvider,
    productConceptsProvider,
    productTechnicalAttributesProvider,
    productCurveGroupAttributesProvider,
    productSAPLister,
    curveGroupDetailsProvider,
    productSpecificationAccessor,
    productSpecificationStore,
    neededAttributesProvider,
    productExistenceProvider,
    accessoryExistenceProvider,
    accessoryExistenceBdPProvider,
    seriesExistenceProvider,
    accessoryCategoriesProvider,
    accessoryClassProductsProvider,
    accessorySpecificationAccessor,
    accessorySpecificationStore,
    newAccessoryClassRecorder,
    curveExistenceProvider,
    grupoCurveExistenceProvider,
    plateTemplatesProvider,
    plateTemplateSectionsProvider,
    plateSectionsCloneProvider,
    plateSectionsProvider,
    productTestsProvider,
    plateProductTestLister,
    plateExistingTemplateLister,
    productSAPSpecificationAccessor,
    plateIconsProvider,
    plateProductTestsProvider,
    plateTemplateStore,
    plateTemplatesStore,
    plateProductAttributesProvider,
    plateProductAndTestAttributesProvider,
    plateSummaryProvider,
    plateUniqueAttributesProvider,
    dashboardUnassignedProductsLister,
    companiesProvider,
    seriesLoadingProcessor,
    crmBulkProcessor,
    seriesExpressProcessor,
    pdfBulkProcessor,
    etimLegacyProcessor,
    http,
    certificateManufacturersProvider,
    certificateEntitiesProvider,
    certificateExistingSeriesProvider,
    certificateTemplatesProvider,
    certificateStore,
    certificateThirdPartyStore,
    certificateThirdPartyUpdateStore,
    certificateUpdate,
    certificatePreview,
    certificatePdfStore,
    certificateExpiryStore,
    certificateGenerateProvider,
    certificateSeriesProductsProvider,
    certificateLegislationsProvider,
    certificateLegislationTypesProvider,
    certificateRegulationsProvider,
    certificateSearchProvider,
    certificateLinkedSeriesProvider,
    certificateListProvider,
    certificateCloneListProvider,
    certificateLanguagesProvider,
    certificateExistingProvider,
    certificateExistingThirdPartyProvider,
    certificateDocumentTypesProvider,
    certificateRegulationsByDocumentTypesProvider,
    certificateRemoval,
    certificateIdNotaCertProvider,
    certificateNotaCertStore,
    certificateDocumentTypeStore,
    certificateRegulationStore,
    certificateDocumentTypeExistenceProvider,
    certificateRegulationExistenceProvider,
    certificateNotaCodeProvider,
    certificateNotaNameProvider,
    aiConversationMessenger,
    aiConversationProcessor,
    etimLister,
    etimSuggester,
    etimVersionsProvider,
    etimListProvider,
    etimSeriesProvider,
    etimClassSeriesProvider,
    etimFeaturesProvider,
    etimCommonFeaturesProvider,
    etimProductsProvider,
    etimGroupedProductsProvider,
    etimCharacteristicsProvider,
    etimStore,
    etimClassCommercialExistenceProvider,
    bmeCatLanguagesProvider,
    bmeCatCompaniesProvider,
    bmeCatProductsProvider,
    bmeCatXMLStructureProvider,
    bmeCatStore,
    manufacturerCompaniesProvider,
    voltagesProvider,
    pitchAngleGenerationService,
  };
}

export default Startup;
